import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { xrStore } from "./xrStore";

/**
 * Header-level "Enter VR" / "Enter AR" controls.
 *
 * Behavior
 * - Probes `navigator.xr.isSessionSupported` for both immersive-vr and
 *   immersive-ar at mount.
 * - Renders a single button with a popover when both are supported, or
 *   a single direct button when only one is.
 * - Hidden entirely (returns null) if WebXR is not available — the page
 *   gracefully falls back to the mouse/scroll experience.
 *
 * Visual
 * - Lives inline next to RainbowKit's ConnectButton.
 * - Subtle live-dot to telegraph "active capability" without being noisy.
 */
export function XREnterButton() {
  const [vr, setVr] = useState<boolean | null>(null);
  const [ar, setAr] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const xr = (navigator as Navigator & { xr?: XRSystem }).xr;
    if (!xr) {
      setVr(false);
      setAr(false);
      return;
    }
    xr.isSessionSupported("immersive-vr").then(
      (s) => !cancelled && setVr(s),
      () => !cancelled && setVr(false),
    );
    xr.isSessionSupported("immersive-ar").then(
      (s) => !cancelled && setAr(s),
      () => !cancelled && setAr(false),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const anySupported = vr || ar;
  if (vr === null || ar === null) return null; // probing
  if (!anySupported) return null;

  const enterVR = () => {
    setOpen(false);
    xrStore.enterVR().catch((e: unknown) => console.error("[XR] enterVR failed", e));
  };
  const enterAR = () => {
    setOpen(false);
    xrStore.enterAR().catch((e: unknown) => console.error("[XR] enterAR failed", e));
  };

  // Single-mode shortcut
  if (vr && !ar)
    return (
      <button
        onClick={enterVR}
        className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl border border-cyan/40 bg-cyan/[0.06] px-4 py-2 text-sm font-semibold text-cyan-glow shadow-[0_0_18px_-8px_rgba(0,242,255,0.6)] transition-all hover:border-cyan hover:shadow-[0_0_28px_-6px_rgba(0,242,255,0.85)]"
        aria-label="Enter VR"
      >
        <span className="live-dot text-cyan" />
        <HeadsetIcon />
        Enter VR
      </button>
    );
  if (ar && !vr)
    return (
      <button
        onClick={enterAR}
        className="group inline-flex items-center gap-2 rounded-xl border border-magenta/40 bg-magenta/[0.06] px-4 py-2 text-sm font-semibold text-magenta-glow"
        aria-label="Enter AR"
      >
        <span className="live-dot text-magenta" />
        <PhoneARIcon />
        Enter AR
      </button>
    );

  // Both — popover
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        className="group inline-flex items-center gap-2 rounded-xl border border-cyan/40 bg-cyan/[0.06] px-4 py-2 text-sm font-semibold text-cyan-glow shadow-[0_0_18px_-8px_rgba(0,242,255,0.6)] transition-all hover:border-cyan hover:shadow-[0_0_28px_-6px_rgba(0,242,255,0.85)]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="live-dot text-cyan" />
        <HeadsetIcon />
        Enter Spatial
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-white/10 bg-black/85 p-1.5 backdrop-blur-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.9)]"
            role="menu"
          >
            <SpatialOption
              onClick={enterVR}
              icon={<HeadsetIcon />}
              title="Immersive VR"
              hint="Vision Pro · Quest 3"
              accent="cyan"
            />
            <SpatialOption
              onClick={enterAR}
              icon={<PhoneARIcon />}
              title="Augmented Reality"
              hint="WebXR passthrough"
              accent="magenta"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SpatialOption({
  onClick,
  icon,
  title,
  hint,
  accent,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  hint: string;
  accent: "cyan" | "magenta";
}) {
  const colors = accent === "cyan" ? "text-cyan-glow" : "text-magenta-glow";
  return (
    <button
      onClick={onClick}
      role="menuitem"
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-white/[0.06]"
    >
      <span className={`grid h-9 w-9 place-items-center rounded-lg border border-white/10 bg-white/[0.04] ${colors}`}>
        {icon}
      </span>
      <span className="flex flex-col">
        <span className="text-sm font-semibold text-white">{title}</span>
        <span className="text-[11px] text-gray-300">{hint}</span>
      </span>
    </button>
  );
}

function HeadsetIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 9c0-1.1.9-2 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-3.5l-2-2h-3l-2 2H5a2 2 0 0 1-2-2V9z" />
      <path d="M12 14v3" />
    </svg>
  );
}

function PhoneARIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="6" y="3" width="12" height="18" rx="2" />
      <path d="M9 7h6M10.5 12.5l1.5-1.5 1.5 1.5M12 11v3" />
    </svg>
  );
}
